/**
 * ConnectorTree — Hierarchical tree navigation for connector entities.
 *
 * Displays a connector → database → schema → table hierarchy with:
 * - Search/filter across all levels
 * - Expand/collapse connector groups
 * - Recent entities section
 * - Loading skeleton state
 * - Status indicators per connector
 *
 * Extracted from EnterpriseDataPage's inline EntityTree for reuse
 * across Data page, Module pages, and any multi-connector UI.
 *
 * Usage:
 * ```tsx
 * <ConnectorTree
 *   connectors={connectors}
 *   selectedConnectorType="hros"
 *   selectedEntity="employees"
 *   onEntitySelect={(type, name, id) => navigate(type, name)}
 *   isLoading={false}
 * />
 * ```
 */

import * as React from 'react';
import { ChevronRight, Clock, Database, Search } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Skeleton } from '../ui/skeleton';

// ─── Types ───────────────────────────────────────────────────────

export interface ConnectorTreeEntity {
  /** Entity/table name (used as key) */
  name: string;
  /** Entity type (e.g., 'table', 'view') */
  type: string;
  /** Number of columns in schema */
  column_count: number;
  /** Approximate row count (optional) */
  row_count?: number;
}

export interface ConnectorTreeNode {
  /** Unique connector ID */
  connector_id: number;
  /** Display name (e.g., 'HROS', 'Tally') */
  name: string;
  /** Connector type slug (e.g., 'hros', 'mssql') */
  connector_type: string;
  /** Connection status */
  status: string;
  /** Entities (tables/views) under this connector */
  entities: ConnectorTreeEntity[];
}

export interface RecentEntity {
  id: number;
  connector_id: number;
  entity_name: string;
  display_name: string;
  last_accessed: string;
  access_count: number;
}

export interface ConnectorTreeProps {
  /** Connector nodes with entities */
  connectors: ConnectorTreeNode[];
  /** Currently selected connector type */
  selectedConnectorType?: string;
  /** Currently selected entity name */
  selectedEntity?: string;
  /** Recently accessed entities (shown at top) */
  recentEntities?: RecentEntity[];
  /** Loading state */
  isLoading?: boolean;
  /** Called when user clicks an entity */
  onEntitySelect: (connectorType: string, entityName: string, connectorId: number) => void;
  /** Additional CSS class */
  className?: string;
}

// ─── Skeleton ────────────────────────────────────────────────────

function TreeSkeleton() {
  return (
    <div className="p-4 space-y-3">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="space-y-2">
          <Skeleton className="h-5 w-3/4" />
          <div className="pl-4 space-y-1.5">
            {Array.from({ length: 4 }).map((_, j) => (
              <Skeleton
                key={j}
                className="h-4"
                style={{ width: `${50 + Math.random() * 40}%` }}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Component ───────────────────────────────────────────────────

export const ConnectorTree = React.forwardRef<HTMLDivElement, ConnectorTreeProps>(
  (
    {
      connectors,
      selectedConnectorType,
      selectedEntity,
      recentEntities,
      isLoading = false,
      onEntitySelect,
      className,
    },
    ref,
  ) => {
    const [search, setSearch] = React.useState('');
    const [expandedConnectors, setExpandedConnectors] = React.useState<Set<number>>(
      new Set(),
    );

    // Auto-expand first connector on initial load
    React.useEffect(() => {
      if (connectors.length > 0 && expandedConnectors.size === 0) {
        setExpandedConnectors(new Set([connectors[0].connector_id]));
      }
    }, [connectors]); // eslint-disable-line react-hooks/exhaustive-deps

    const toggleConnector = React.useCallback((id: number) => {
      setExpandedConnectors((prev) => {
        const next = new Set(prev);
        if (next.has(id)) {
          next.delete(id);
        } else {
          next.add(id);
        }
        return next;
      });
    }, []);

    // Filter connectors + entities by search term
    const filteredConnectors = React.useMemo(() => {
      if (!search) return connectors;
      const term = search.toLowerCase();
      return connectors
        .map((c) => ({
          ...c,
          entities: c.entities.filter((e) =>
            e.name.toLowerCase().includes(term),
          ),
        }))
        .filter(
          (c) =>
            c.entities.length > 0 ||
            c.name.toLowerCase().includes(term),
        );
    }, [connectors, search]);

    if (isLoading) {
      return (
        <div ref={ref} className={cn('flex flex-col h-full', className)}>
          <TreeSkeleton />
        </div>
      );
    }

    return (
      <div ref={ref} className={cn('flex flex-col h-full', className)}>
        {/* Search */}
        <div className="p-3 border-b border-[var(--color-border)]">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[var(--color-muted-foreground)]" />
            <input
              type="text"
              placeholder="Filter entities..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className={cn(
                'w-full pl-8 pr-3 py-1.5 text-sm border rounded',
                'bg-[var(--color-background)] border-[var(--color-border)]',
                'focus:outline-none focus:ring-1 focus:ring-[var(--color-ring)]',
                'placeholder:text-[var(--color-muted-foreground)]',
              )}
            />
          </div>
        </div>

        {/* Recent entities */}
        {recentEntities && recentEntities.length > 0 && !search && (
          <div className="px-3 py-2 border-b border-[var(--color-border)]">
            <p className="text-xs font-medium text-[var(--color-muted-foreground)] mb-1.5 flex items-center gap-1">
              <Clock className="h-3 w-3" /> Recent
            </p>
            {recentEntities.slice(0, 5).map((re) => {
              const conn = connectors.find(
                (c) => c.connector_id === re.connector_id,
              );
              return (
                <button
                  key={re.id}
                  onClick={() =>
                    conn &&
                    onEntitySelect(
                      conn.connector_type,
                      re.entity_name,
                      re.connector_id,
                    )
                  }
                  className={cn(
                    'block w-full text-left px-2 py-1 text-sm rounded truncate',
                    'hover:bg-[var(--color-muted)]',
                    selectedEntity === re.entity_name &&
                      'bg-[var(--color-primary)]/10 text-[var(--color-primary)] font-medium',
                  )}
                >
                  {re.display_name}
                </button>
              );
            })}
          </div>
        )}

        {/* Connector tree */}
        <div className="flex-1 overflow-auto py-2">
          {filteredConnectors.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-[var(--color-muted-foreground)]">
              No connectors found
            </div>
          ) : (
            filteredConnectors.map((conn) => {
              const isExpanded = expandedConnectors.has(conn.connector_id);
              const statusColor =
                conn.status === 'active' || conn.status === 'connected'
                  ? 'bg-green-500'
                  : conn.status === 'error'
                    ? 'bg-red-500'
                    : 'bg-gray-400';

              return (
                <div key={conn.connector_id}>
                  {/* Connector header */}
                  <button
                    onClick={() => toggleConnector(conn.connector_id)}
                    className={cn(
                      'flex items-center gap-2 w-full px-3 py-1.5 text-sm font-medium',
                      'hover:bg-[var(--color-muted)] transition-colors',
                    )}
                  >
                    <ChevronRight
                      className={cn(
                        'h-3.5 w-3.5 transition-transform',
                        isExpanded && 'rotate-90',
                      )}
                    />
                    <span className={cn('h-2 w-2 rounded-full', statusColor)} />
                    <span className="truncate">{conn.name}</span>
                    <span className="ml-auto text-xs text-[var(--color-muted-foreground)]">
                      {conn.entities.length}
                    </span>
                  </button>

                  {/* Entities */}
                  {isExpanded && (
                    <div className="ml-4 pl-3 border-l border-[var(--color-border)]">
                      {conn.entities.map((entity) => {
                        const isSelected =
                          selectedConnectorType === conn.connector_type &&
                          selectedEntity === entity.name;
                        return (
                          <button
                            key={entity.name}
                            onClick={() =>
                              onEntitySelect(
                                conn.connector_type,
                                entity.name,
                                conn.connector_id,
                              )
                            }
                            className={cn(
                              'flex items-center justify-between w-full px-2 py-1 text-sm rounded',
                              isSelected
                                ? 'bg-[var(--color-primary)]/10 text-[var(--color-primary)] font-medium'
                                : 'hover:bg-[var(--color-muted)] text-[var(--color-foreground)]',
                            )}
                          >
                            <span className="truncate">
                              {entity.name.replace(/_/g, ' ')}
                            </span>
                            {entity.column_count > 0 && (
                              <span className="text-xs text-[var(--color-muted-foreground)] ml-2">
                                {entity.column_count} cols
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    );
  },
);

ConnectorTree.displayName = 'ConnectorTree';
